'use client';

import { motion } from 'motion/react';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import SectionHeader from '@/components/shared/section-header';
import SpacesCard from '@/components/sections/spaces-we-transform/card';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import type { SpacesWeTransformSectionContent } from '@/sanity/lib/sections';

export default function SpacesWeTransformSection({
  content,
}: {
  content?: SpacesWeTransformSectionContent | null;
}) {
  const {
    sectionCardRevealVariants,
    sectionGridRevealVariants,
    sectionSequenceBlockVariants,
    sectionSequenceRevealVariants,
  } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  const { header } = content ?? {};
  const { primary, secondary } = sectionCtaPairFromContent(content?.showCTA, content?.ctaButtons);
  const cards =
    content?.showCards === false
      ? []
      : (content?.cards ?? []).flatMap((card) =>
          card.title && card.image?.src
            ? [
                {
                  src: card.image.src,
                  alt: card.image.alt,
                  title: card.title,
                  description: card.description ?? undefined,
                },
              ]
            : [],
        );

  if (!content || content.show === false) return null;

  return (
    <section
      id="spaces-we-transform"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-background py-6 sm:py-12 xl:py-16 2xl:py-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0">
        <motion.div
          className="space-y-6 sm:space-y-12"
          {...scrollReveal}
          variants={sectionSequenceRevealVariants}
        >
          {content.showHeader !== false ? (
            <SectionHeader
              sequenceChild
              content={{
                eyebrow: header?.tagline,
                description: header?.subheading,
              }}
              titleSlot={
                <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-foreground">
                  <SanityInlineText
                    blocks={header?.heading}
                    emphasisClassName="italic font-normal text-[#948B7F] dark:text-[#FCFAD7]"
                  />
                </h2>
              }
              eyebrowClassName="text-[#948B7F] dark:text-[#FCFAD7]"
              emphasisClassName="text-[#948B7F] dark:text-[#FCFAD7]"
              headingClassName="text-foreground"
              subheadingClassName="text-muted-foreground"
            />
          ) : null}
          {primary ? (
            <motion.div variants={sectionSequenceBlockVariants}>
              <SectionCtaPair
                primary={primary}
                secondary={secondary}
                trackingLocation="spaces_we_transform"
              />
            </motion.div>
          ) : null}
          {cards.length ? (
            <motion.div
              variants={sectionGridRevealVariants}
              className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {cards.map((card, index) => (
                <motion.div key={`${card.title}-${index}`} variants={sectionCardRevealVariants}>
                  <SpacesCard card={card} />
                </motion.div>
              ))}
            </motion.div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}
